/**
 * PHRASE BLOOM のプリセットペイロード（直列化・検証）。
 *
 * ツールプリセット基盤（`useToolPresetBrowser` + `/api/me/tools/.../presets`）に
 * 保存する JSON の組み立てと、読み込み時のサニタイズを担う。
 *
 * サニタイズ方針: 「読めるところは読む」。
 *  - 各フィールドをデフォルト値とマージし、数値はレンジにクランプ、
 *    enum 系 ID は選択肢に無ければデフォルトへフォールバックする
 *  - 古いプリセット（フィールド不足）や未来のプリセット（未知フィールド）
 *    を読み込んでもツールが壊れないことを保証する
 *    （旧 SOUND C 系フィールドを含むプリセットも安全に読み捨てる）
 *  - ペイロード全体がオブジェクトでない場合のみ null（読込失敗）を返す
 */

import { SCALES, type CadenceId } from "./musicTheory";
import {
  DEFAULT_LAYER_PARAMS,
  TWIN_OFFSETS,
  type LayerParams,
  type SoundAMode,
} from "./arrangement";
import {
  DEFAULT_PARAMS,
  END_POINTS,
  type PhraseParams,
} from "./phraseGenerator";
import {
  DEFAULT_SYNTH_PARAMS,
  DELAY_DIVISIONS,
  DELAY_MS_MAX,
  DELAY_MS_MIN,
  GLIDE_MS_MAX,
  GLIDE_MS_MIN,
  GOLD_RATE_MAX,
  GOLD_RATE_MIN,
  type DelayMode,
  type OscType,
  type SynthParams,
} from "./synthVoice";

export const PHRASE_BLOOM_TOOL_ID = "phrase-bloom";

/** ペイロード形式のバージョン。互換の無い構造変更をしたら上げる。 */
export const PHRASE_BLOOM_SCHEMA_VERSION = 1;

export interface PhraseBloomPresetPayload {
  params: PhraseParams;
  layerParams: LayerParams;
  synthParams: SynthParams;
}

/** 現在のツール状態をプリセットペイロードに直列化する。 */
export const buildPresetPayload = (
  params: PhraseParams,
  layerParams: LayerParams,
  synthParams: SynthParams,
): PhraseBloomPresetPayload => ({
  params,
  layerParams,
  synthParams,
});

// ===== サニタイズ用プリミティブ =====

const clampNumber = (
  value: unknown,
  fallback: number,
  min: number,
  max: number,
  { integer = true }: { integer?: boolean } = {},
): number => {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  const clamped = Math.min(max, Math.max(min, value));
  return integer ? Math.round(clamped) : clamped;
};

const asBoolean = (value: unknown, fallback: boolean): boolean =>
  typeof value === "boolean" ? value : fallback;

const oneOf = <T extends string>(
  value: unknown,
  valid: readonly T[],
  fallback: T,
): T =>
  typeof value === "string" && (valid as readonly string[]).includes(value)
    ? (value as T)
    : fallback;

const OSC_TYPES: readonly OscType[] = [
  "sine",
  "triangle",
  "sawtooth",
  "square",
  "noise",
];

const CADENCE_IDS: readonly CadenceId[] = ["tonic", "subdominant", "dominant"];

const SOUND_A_MODES: readonly SoundAMode[] = ["off", "unison", "harmony"];

const DELAY_MODES: readonly DelayMode[] = ["sync", "ms"];

const SCALE_IDS = SCALES.map((s) => s.id);
const END_POINT_IDS = END_POINTS.map((e) => e.id);
const TWIN_OFFSET_IDS = TWIN_OFFSETS.map((o) => o.id);
const DELAY_DIVISION_IDS = DELAY_DIVISIONS.map((d) => d.id);

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

// ===== セクション別サニタイズ =====

const sanitizeParams = (raw: unknown): PhraseParams => {
  const r = asRecord(raw);
  const d = DEFAULT_PARAMS;
  return {
    bpm: clampNumber(r.bpm, d.bpm, 60, 240),
    noteCount: clampNumber(r.noteCount, d.noteCount, 2, 32),
    endPointId: oneOf(r.endPointId, END_POINT_IDS, d.endPointId),
    riseWeight: clampNumber(r.riseWeight, d.riseWeight, 0, 100),
    keepWeight: clampNumber(r.keepWeight, d.keepWeight, 0, 100),
    fallWeight: clampNumber(r.fallWeight, d.fallWeight, 0, 100),
    accel: clampNumber(r.accel, d.accel, -50, 50),
    gate: clampNumber(r.gate, d.gate, 10, 200),
    glide: asBoolean(r.glide, d.glide),
    cadence: oneOf(r.cadence, CADENCE_IDS, d.cadence),
    rootPc: clampNumber(r.rootPc, d.rootPc, 0, 11),
    baseOctave: clampNumber(r.baseOctave, d.baseOctave, 0, 6),
    scaleId: oneOf(r.scaleId, SCALE_IDS, d.scaleId),
    seed: clampNumber(r.seed, d.seed, 0, 999999),
  };
};

const sanitizeLayerParams = (raw: unknown): LayerParams => {
  const r = asRecord(raw);
  const d = DEFAULT_LAYER_PARAMS;
  return {
    soundAMode: oneOf(r.soundAMode, SOUND_A_MODES, d.soundAMode),
    soundBEnabled: asBoolean(r.soundBEnabled, d.soundBEnabled),
    soundBOffsetId: oneOf(r.soundBOffsetId, TWIN_OFFSET_IDS, d.soundBOffsetId),
  };
};

const sanitizeSynthParams = (raw: unknown): SynthParams => {
  const r = asRecord(raw);
  const d = DEFAULT_SYNTH_PARAMS;
  return {
    osc: oneOf(r.osc, OSC_TYPES, d.osc),
    goldOn: asBoolean(r.goldOn, d.goldOn),
    goldRateHz: clampNumber(r.goldRateHz, d.goldRateHz, GOLD_RATE_MIN, GOLD_RATE_MAX),
    soundAOsc: oneOf(r.soundAOsc, OSC_TYPES, d.soundAOsc),
    soundBOsc: oneOf(r.soundBOsc, OSC_TYPES, d.soundBOsc),
    glideMs: clampNumber(r.glideMs, d.glideMs, GLIDE_MS_MIN, GLIDE_MS_MAX),
    delayMode: oneOf(r.delayMode, DELAY_MODES, d.delayMode),
    delayDivisionId: oneOf(r.delayDivisionId, DELAY_DIVISION_IDS, d.delayDivisionId),
    delayMs: clampNumber(r.delayMs, d.delayMs, DELAY_MS_MIN, DELAY_MS_MAX),
    delayFeedback: clampNumber(r.delayFeedback, d.delayFeedback, 0, 0.9, { integer: false }),
    delayMix: clampNumber(r.delayMix, d.delayMix, 0, 1, { integer: false }),
    clipOn: asBoolean(r.clipOn, d.clipOn),
    clipDrive: clampNumber(r.clipDrive, d.clipDrive, 0, 24),
    kaitlynOn: asBoolean(r.kaitlynOn, d.kaitlynOn),
  };
};

/**
 * 保存済みペイロードを検証し、安全な `PhraseBloomPresetPayload` に正規化する。
 *
 * ペイロード全体がオブジェクトでない場合のみ null（読込失敗）。
 * それ以外は欠損・不正フィールドをデフォルト値で補完して返す。
 * スキーマは現状 v1 のみ。互換の無い構造変更をしたら schema_version 引数を
 * 受けて分岐を追加する。
 */
export const sanitizePresetPayload = (
  raw: unknown,
): PhraseBloomPresetPayload | null => {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  return {
    params: sanitizeParams(record.params),
    layerParams: sanitizeLayerParams(record.layerParams),
    synthParams: sanitizeSynthParams(record.synthParams),
  };
};
